import { type Accessor, createMemo } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import {
  getMyOrganizationsLocalQuery,
  handleApiResponse,
  type OrganizationRecord,
  type OrgScope,
  queryKeys,
} from "@fieldcheck/shared";
import { desc, organization, type PowerSyncDrizzleDB } from "@fieldcheck/powersync";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import {
  activeScopeIdFor,
  PERSONAL_SCOPE,
  resolveActiveOrgId,
  setActiveScopeIdFor,
} from "@/features/organizations/active-org";

/** Switcher entry: a real org or the Personal workspace (`organization_id` NULL). */
export interface OrgScopeEntry {
  /** Scope ID: real org ID, or `""` (Personal). */
  id: string;
  /** Display name. */
  name: string;
  /** `true` for the Personal workspace (not a real organization). */
  personal: boolean;
}

export interface ActiveOrgContext {
  /** User organizations, synchronized locally (member). */
  orgs: Accessor<OrganizationRecord[]>;
  /** Switcher entries: [Personal, ...orgs]. */
  scopes: Accessor<OrgScopeEntry[]>;
  /**
   * ACTIVE data scope for the domain list queries: `{ organizationId }`
   * where `null` = Personal (`organization_id IS NULL`). Validated against what
   * exists locally (self-healing: falls back to Personal if the pointer is stale).
   * All views (sites/templates/inspections/findings) derive from here, so
   * a switch reactively re-scopes fieldwork.
   */
  scope: Accessor<OrgScope>;
  /** Active `organization_id`, or `null` when the active scope is Personal. */
  activeOrgId: Accessor<string | null>;
  /** Active organization, or `null` if the active scope is Personal. */
  activeOrg: Accessor<OrganizationRecord | null>;
  /** Active scope (org or Personal) as a switcher entry. */
  activeScope: Accessor<OrgScopeEntry>;
  /**
   * Changes the active scope (persisted per user; re-scopes the domain). Pass
   * `""` (`PERSONAL_SCOPE`) to return to Personal. Notifies the server
   * best-effort (`setActiveOrganization`) so the session remembers the context.
   */
  setActiveOrg: (scopeId: string) => void;
  /**
   * Creates an organization (atomic org+member server-side, CC-3), waits for it
   * to sync locally, makes it ACTIVE, and returns its id. Creation path for both
   * the FIRST organization (onboarding) and additional ones.
   */
  bootstrapOrg: (name: string) => Promise<string>;
}

/**
 * FieldCheck multi-organization context: the active inspection organization
 * and the user's set of organizations (MULTI_ORG_SWITCHER guide,
 * donors comandero/stockpilot).
 *
 * Org-centric model: each domain row (site/template/inspection/finding)
 * carries `organization_id` (one org) or NULL (Personal). The local replica
 * downloads EVERYTHING the sync rules authorize (my rows + rows from ALL my
 * orgs); without scope, views mix activity from all organizations. The active
 * scope is a preference persisted per user (`active-org.ts`) validated
 * against the orgs locally; list queries derive from `scope()`, so a
 * switch reactively re-scopes fieldwork without reloading.
 */
export function useActiveContext(): ActiveOrgContext {
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const session = useSession();

  const orgsQuery = createPowerSyncQuery<OrganizationRecord>({
    queryKey: () => queryKeys.organizations.localList(),
    db: () => db(),
    queryFn: () => {
      const d = drizzle();
      return d ? getMyOrganizationsLocalQuery(d) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const orgs = createMemo<OrganizationRecord[]>(() => orgsQuery.data ?? []);

  const scopes = createMemo<OrgScopeEntry[]>(() => [
    { id: PERSONAL_SCOPE, name: "Personal", personal: true },
    ...orgs().map((o) => ({ id: o.id, name: o.name ?? o.slug ?? o.id, personal: false })),
  ]);

  const activeOrgId = createMemo<string | null>(() => {
    const uid = session.userId();
    const stored = activeScopeIdFor(uid);
    return resolveActiveOrgId(
      stored,
      orgs().map((o) => o.id),
    );
  });

  const scope = createMemo<OrgScope>(() => ({ organizationId: activeOrgId() }));

  const activeOrg = createMemo<OrganizationRecord | null>(() => {
    const id = activeOrgId();
    return id ? (orgs().find((o) => o.id === id) ?? null) : null;
  });

  const activeScope = createMemo<OrgScopeEntry>(() => {
    const org = activeOrg();
    if (org) {
      return { id: org.id, name: org.name ?? org.slug ?? org.id, personal: false };
    }
    return { id: PERSONAL_SCOPE, name: "Personal", personal: true };
  });

  const setActiveOrg = (scopeId: string): void => {
    setActiveScopeIdFor(session.userId(), scopeId);
    // Best-effort: remember context in the server-side session (membership guard
    // lives in the handler; a failure here does not break the local scope).
    void api
      .POST("/api/org/setActiveOrganization", {
        body: { organizationId: scopeId === PERSONAL_SCOPE ? null : scopeId },
      })
      .catch(() => {
        // The local scope has already changed; server notification is optional.
      });
  };

  async function bootstrapOrg(name: string): Promise<string> {
    const d = drizzle();
    if (!d) {
      throw new Error("Local database is not ready");
    }
    // There may already be orgs locally: capture the previous IDs to wait for
    // the NEW org (not "any org", which would resolve immediately with another).
    const before = new Set(orgs().map((existing) => existing.id));
    const trimmed = name.trim();
    const slug =
      trimmed
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/gu, "-")
        .replaceAll(/^-+|-+$/gu, "")
        .slice(0, 40) || `org-${Date.now()}`;
    // `openapi-fetch` does NOT throw on 4xx/5xx (returns `{error}`): without this
    // wrapper, a 401 (guest without an account creating an org) died silently and the
    // poll below would wait for an org that never arrives — the "Create" button
    // would stay stuck without feedback (CC-30 / CC-8 family). `handleApiResponse`
    // throws on non-2xx and the switcher paints the error inline.
    await handleApiResponse(
      api.POST("/api/org/createOrganization", {
        body: { name: trimmed, slug },
      }),
    );
    // The response only confirms `success`; the id arrives when the org syncs
    // locally (server-side creates atomic org+member, CC-3).
    const orgId = await waitForNewOrgLocal(d, before);
    if (!orgId) {
      throw new Error("Organization did not sync to local in time");
    }
    // The new org becomes active (onboarding: it was the only one; additional
    // creation: the user requested it — the switch is the expectation).
    setActiveOrg(orgId);
    return orgId;
  }

  return {
    orgs,
    scopes,
    scope,
    activeOrgId,
    activeOrg,
    activeScope,
    setActiveOrg,
    bootstrapOrg,
  };
}

const POLL_INTERVAL_MS = 200;
const MAX_WAIT_MS = 30_000;

/**
 * Waits (with polling over the local replica) for an organization
 * that was NOT in `before` to appear and returns its id. Queries the DB directly
 * (not the reactive accessor) to avoid reading signals outside a tracked scope.
 */
async function waitForNewOrgLocal(
  d: PowerSyncDrizzleDB,
  before: ReadonlySet<string>,
): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < MAX_WAIT_MS) {
    const rows = await d.select().from(organization).orderBy(desc(organization.created_at));
    const fresh = rows.find((row) => !before.has(row.id));
    if (fresh?.id) {
      return fresh.id;
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return null;
}
